import { FC, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import {
  ActionsMenu,
  Loader,
  NothingFoundAlert,
  PageContentLayout,
  PageHeaderLayout,
  SideMenu,
} from 'melp-design/components';
import { ROUTES } from 'config';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useRouteParams } from 'utils/useIdParam';
import { useConfirm } from 'store/confirm';
import { useAdminShopItem, useRemoveShopItem } from 'store/admin-shop';
import { ContentReviewPendingChip } from 'containers/admin';

export const AdminShopItemLayout: FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const { t } = useTranslation();
  const { path } = useRouteMatch();
  const history = useHistory();
  const { id: shopItemId } = useRouteParams(
    ROUTES.admin.shopItems.details.settings,
  );
  const { confirmation } = useConfirm();

  const { data: shopItem, isLoading } = useAdminShopItem(shopItemId);

  const { mutate: removeShopItem, isLoading: isRemoving } = useRemoveShopItem();

  if (isLoading) return <Loader />;
  if (!shopItem) return <NothingFoundAlert />;

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('menu.items'),
            url: ROUTES.admin.shopItems.list,
          },
          {
            label: shopItem ? shopItem.name : '',
          },
        ]}
        under={
          shopItem ? (
            <ContentReviewPendingChip liveEntityId={shopItemId} />
          ) : undefined
        }
        side={
          shopItem ? (
            <ActionsMenu
              items={[
                {
                  label: t('actions.delete'),
                  onClick: async () => {
                    const { confirmed } = await confirmation(
                      t('common.areYouSureToDelete', {
                        title: shopItem.name,
                        interpolation: { escapeValue: false },
                      }),
                    );

                    if (confirmed) {
                      removeShopItem(
                        { shopItemId },
                        {
                          onSuccess: () => {
                            history.push(ROUTES.admin.shopItems.list);
                          },
                        },
                      );
                    }
                  },
                  color: 'error',
                  disabled: isRemoving,
                },
              ]}
            />
          ) : null
        }
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'settings',
                label: t('marketplaceItems.marketplaceItemSettings'),
                url: ROUTES.admin.shopItems.details.settings.replace(
                  ':id',
                  shopItemId,
                ),
                isActive: path === ROUTES.admin.shopItems.details.settings,
              },
              {
                key: 'prices',
                label: t('marketplaceItems.marketplaceItemPriceSettings'),
                url: ROUTES.admin.shopItems.details.prices.replace(
                  ':id',
                  shopItemId,
                ),
                isActive: path === ROUTES.admin.shopItems.details.prices,
              },
              {
                key: 'content',
                label: t('marketplaceItems.marketplaceItemContent'),
                url: ROUTES.admin.shopItems.details.content.replace(
                  ':id',
                  shopItemId,
                ),
                isActive: path === ROUTES.admin.shopItems.details.content,
              },
              ...(shopItem &&
              ![
                'rSNsoltYPWADykKH6OXS1',
                '7QwtuXKgphbS2XXkZnhfm',
                'iOoEwjyXfBBEDRKj76paj',
                'Hnn-wm79H_AA_hfSEupNP',
              ].includes(shopItem.supplier.id)
                ? [
                    {
                      key: 'vouchers',
                      label: t('shop.vouchers.title'),
                      url: ROUTES.admin.shopItems.details.vouchers.replace(
                        ':id',
                        shopItemId,
                      ),
                      isActive:
                        path === ROUTES.admin.shopItems.details.vouchers,
                    } as const,
                  ]
                : []),
            ]}
          />
        }
      >
        {children}
      </PageContentLayout>
    </Stack>
  );
};
